/**
 * Turning an API error body into something worth showing a user.
 *
 * `validate()` in @fastn/http answers a Zod failure with `error` set to
 * "Request validation failed" followed by a short summary of up to three
 * failures, and puts every field-level reason in `details[]` (mirrored as
 * `issues[]` for older consumers). The full list is what the user needs, so it
 * is rendered from `details[]` here, and the summary is set aside rather than
 * printed in front of the same reasons a second time.
 *
 * The shapes this has to survive, all of them real in this repo:
 *
 *   { error: "Request validation failed: name: ...", details: [{path,message}], issues: [...] }
 *       validate() — the common case. Zod v4 messages never name the field
 *       ("Invalid input: expected string, received undefined"), so the path has
 *       to be put back in front of them or the user still cannot act. Older
 *       servers send the bare "Request validation failed".
 *   { error: "Invalid request body", details: <raw zod issues, path is string[]> }
 *       services/api/src/routes/orchestrator-feedback.ts
 *   { error: "Input validation failed", details: ["email: required field is missing"] }
 *       services/api/src/routes/actions.ts — details are plain strings
 *   { error: "Unknown entitlement keys", details: { unknown: [...], known: [...] } }
 *       services/api/src/routes/entitlements.ts — details is an OBJECT and the
 *       error is already specific; both mean "leave this message alone"
 *   { error: { code, message, details } }
 *       services/event writes this literal shape in 64 places (triggers,
 *       schedulers, DLQ, monitoring), and jsonError() in @fastn/http emits it
 *       for every caller of createErrorResponder(). Lifting the OBJECT into
 *       `new ApiError(status, message)` let Error coerce it, so all of those
 *       rendered literally "[object Object]"
 *   { error: "You cannot remove the last owner" }
 *       an endpoint that already says something useful — returned verbatim
 *   no body at all / an HTML error page from a proxy
 *       nothing parses; the status is all there is
 *
 * Dependency-free on purpose: imported by a node-environment unit test, and
 * lib/api.ts pulls in the auth layer, which touches `window` at module scope.
 */

interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
  details?: unknown;
  issues?: unknown;
}

/**
 * At most this many field failures are shown; the rest are counted.
 *
 * Six is the largest top-level field count any request schema in the repo can
 * fail on at once (CreatePlatformProviderSchema, services/api/src/routes/
 * connectors.ts) — everything above that comes from repeated
 * `z.array(z.object(...))` sections that are gated client-side before send. So
 * the cap can only elide a bulk payload the user is not looking at, never a box
 * on the form in front of them.
 */
const MAX_DETAILS = 6;

/** A single detail longer than this is cut — a toast is not a log viewer. */
const MAX_DETAIL_CHARS = 160;

/**
 * And the joined details are cut again. Six at the per-detail cap is ~1KB,
 * which is a page of text in a toast; this holds six ordinary field messages
 * uncut and only bites when they are individually long.
 */
const MAX_JOINED_CHARS = 400;

/**
 * The whole message, once a server-authored `top` is carried in front of the
 * details. `top` is pre-existing output and is never cut, so it is the details
 * that give way — down to nothing if `top` alone already fills the allowance.
 */
const MAX_MESSAGE_CHARS = 600;

/** Below this there is no room to say anything useful, so no detail is added. */
const MIN_DETAIL_CHARS = 32;

const DETAIL_SEPARATOR = "; ";

/**
 * A machine-readable error code rather than something written for a reader:
 * lowercase, snake_case, at least two segments, no spaces. `quota_exceeded`,
 * `rate_limited`, `not_found`. Single words are excluded on purpose — "forbidden"
 * or "conflict" alone is still the best text some routes give us.
 */
const MACHINE_CODE = /^[a-z0-9]+(?:_[a-z0-9]+)+$/;

/**
 * Top-level strings whose entire content is "something was invalid". When one of
 * these is all the server sent, a field-level detail replaces it outright rather
 * than being prefixed by it. Anything not on this list is treated as a real
 * message and is never dropped.
 */
const GENERIC_TOP_MESSAGES = new Set([
  "request validation failed",
  "validation failed",
  "invalid request",
  "invalid request body",
  "invalid input",
  "input validation failed",
]);

/**
 * How validate()'s `error` opens when it carries its own summary of `details[]`.
 * With a field list beside it, only this lead is kept: the list is rendered
 * below, and the server's summary has not been through INTERNAL_TEXT.
 */
const VALIDATION_SUMMARY_LEAD = "request validation failed:";

/**
 * Server internals that must never be painted into the UI. Some routes put a
 * driver or IdP message straight into `details` (BB-066 fixes that at the
 * source); until then the dashboard refuses to render one, and falls back to
 * whatever the top-level message said. This is UI hygiene, not containment —
 * the text is still in the response body.
 */
const INTERNAL_TEXT = [
  /\bat [\w$.<>[\]]+ \(/,                              // stack frame
  /\.(?:ts|js|mjs|cjs):\d+:\d+/,                       // file:line:col
  // Unquoted, so a quoted path inside a Zod enum message is not a server path.
  /(?:^|[\s(])\/(?:usr|var|etc|opt|tmp|app|home|Users|src|dist|node_modules)\//,
  // The driver's own phrasing, not merely a quoted table or column name —
  // field-mapping copy quotes those legitimately.
  /\b(?:relation|constraint|type|schema|column) "[^"]+" (?:does not exist|already exists)/i,
  /duplicate key value|violates \w+ constraint/i,
  /\b(?:SELECT|INSERT INTO|UPDATE|DELETE FROM)\b[\s\S]*\b(?:FROM|INTO|SET|WHERE)\b/, // SQL echo
  /\b(?:ECONNREFUSED|ECONNRESET|ENOTFOUND|ETIMEDOUT|EAI_AGAIN|EPIPE|ENOENT|EACCES|EPERM)\b/,
  /(?:postgres|postgresql|redis|mongodb|amqp):\/\//i,  // connection string
  // A whole internal URL. A bare "localhost:3000" is what a URL-field validator
  // legitimately tells the user not to submit, and a bare dotted quad is a
  // version number as often as an address.
  /\bhttps?:\/\/(?:localhost|127\.0\.0\.1|(?:\d{1,3}\.){3}\d{1,3}|[a-z0-9-]+:\d{2,5})/i,
  /\brealms\/[\w-]+\/protocol|\bjwks\b/i,             // identity provider
  /\beyJ[A-Za-z0-9_-]{10,}\./,                        // JWT
];

/** Zod v4's canonical "you did not send this field" message, on any type. */
const MISSING_FIELD = /^Invalid input: expected .+, received undefined$/;

function looksInternal(text: string): boolean {
  return INTERNAL_TEXT.some((re) => re.test(text));
}

/** Cut to at most `limit` characters INCLUDING the ellipsis. */
function truncate(text: string, limit = MAX_DETAIL_CHARS): string {
  if (text.length <= limit) return text;
  let cut = text.slice(0, limit - 1);
  const lastSpace = cut.lastIndexOf(" ");
  // Only back up to a word boundary when it costs almost nothing; a detail with
  // one late space should not forfeit half its allowance to end on a word.
  if (lastSpace > limit * 0.9) cut = cut.slice(0, lastSpace);
  // A slice can land between the halves of a surrogate pair. The lone high
  // surrogate left behind renders as a replacement glyph and makes the string
  // ill-formed, so anything that later URI-encodes the message throws.
  const tail = cut.charCodeAt(cut.length - 1);
  if (tail >= 0xd800 && tail <= 0xdbff) cut = cut.slice(0, -1);
  return `${cut.trimEnd()}…`;
}

/** Does `message` start with `token` as a whole word? */
function opensWith(message: string, token: string): boolean {
  if (!token) return false;
  const lower = message.toLowerCase();
  if (!lower.startsWith(token.toLowerCase())) return false;
  return !/[a-z0-9_]/.test(lower.charAt(token.length));
}

/** `path` is a dotted string from validate() but a raw array from bare zod issues. */
function pathOf(entry: { path?: unknown }): string {
  const { path } = entry;
  if (typeof path === "string") return path.trim();
  if (Array.isArray(path)) return path.filter((s) => s !== "" && s != null).join(".");
  return "";
}

function renderDetail(entry: unknown): string | null {
  const raw = typeof entry === "string"
    ? entry
    : entry && typeof entry === "object"
      ? (entry as { message?: unknown }).message
      : undefined;
  if (typeof raw !== "string") return null;

  const message = raw.trim();
  if (!message || looksInternal(message)) return null;

  const path = typeof entry === "object" && entry !== null ? pathOf(entry as { path?: unknown }) : "";
  if (!path) return truncate(message);

  if (MISSING_FIELD.test(message)) return `${path} is required`;

  // A custom .refine() message usually opens with the field name; prefixing it
  // again reads "expires_at: expires_at must be in the future". Whole-token
  // only — a short path like "id" must not swallow "identifier is invalid".
  const leaf = path.split(".").pop() ?? path;
  const named = opensWith(message, path) || opensWith(message, leaf);
  return truncate(named ? message : `${path}: ${message}`);
}

/**
 * Every array that could be a field list, in preference order. `details` and
 * `issues` are the same list on a validate() envelope, so the first one that
 * renders anything settles it; a `details` that is an object (entitlements,
 * quota) is not a field list and never appears here.
 */
function detailCandidates(body: ApiErrorBody): unknown[][] {
  const nested = body.error && typeof body.error === "object"
    ? (body.error as { details?: unknown }).details
    : undefined;
  return [body.details, body.issues, nested].filter(
    (c): c is unknown[] => Array.isArray(c) && c.length > 0,
  );
}

function renderAll(entries: unknown[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of entries) {
    const line = renderDetail(entry);
    if (line && !seen.has(line)) {
      seen.add(line);
      out.push(line);
    }
  }
  return out;
}

function topMessage(body: unknown): string {
  // A JSON body that is just a string is new output — the old read ignored it —
  // so it gets both the cap and the leak filter. `error` and `message` are
  // pre-existing output and stay exactly as they were: dropping one would take
  // information away from routes that cannot be enumerated from here, and the
  // server-side leak is BB-066's to fix.
  if (typeof body === "string") {
    const text = body.trim();
    return looksInternal(text) ? "" : truncate(text);
  }
  if (!body || typeof body !== "object") return "";
  const b = body as ApiErrorBody;
  if (typeof b.error === "string") {
    const code = b.error.trim();
    /* A machine code with a sentence beside it: prefer the sentence.
       Several routes answer { error: "<snake_case_code>", message: "<prose>" } —
       the API-key quota refusal is
         { error: "quota_exceeded",
           message: "This organization already has 3 of 2 API keys. Revoke one,
                     or upgrade the plan." }
       — and returning `error` first put the bare token "quota_exceeded" in
       front of the user while the explanation sat unread in the same response.
       Deliberately narrow: only a lowercase snake_case token with no spaces is
       treated as a code, so prose in `error` (which is the common case, e.g.
       "You cannot remove the last owner") still wins as it always has. */
    if (MACHINE_CODE.test(code) && typeof b.message === "string" && b.message.trim()) {
      return b.message.trim();
    }
    return code;
  }
  if (b.error && typeof b.error === "object") {
    const nested = (b.error as { message?: unknown; code?: unknown });
    if (typeof nested.message === "string" && nested.message.trim()) return nested.message.trim();
    // A jsonError() envelope carrying only a code. Not pretty, but "RATE_LIMITED"
    // is something the reader can search for and act on, where the alternative
    // is a bare "Request failed (HTTP 429)".
    if (typeof nested.code === "string" && nested.code.trim()) return nested.code.trim();
  }
  if (typeof b.message === "string") return b.message.trim();
  return "";
}

/**
 * Join what fits and count what did not.
 *
 * Two elisions happen here — the count cap and the character budget — and
 * "(+N more)" has to describe BOTH. Deriving it from the pre-truncation slice
 * would report what was intended rather than what survived, so the count cap
 * would degrade honestly while the character cap quietly lied.
 */
function joinWithinBudget(rendered: string[], budget: number): string {
  const lines: string[] = [];
  let used = 0;
  for (const line of rendered.slice(0, MAX_DETAILS)) {
    const cost = lines.length === 0 ? line.length : line.length + DETAIL_SEPARATOR.length;
    // The first line goes in whatever its length; truncate() bounds it below.
    // Without that, an over-long lone detail would yield "(+1 more)" alone.
    if (lines.length > 0 && used + cost > budget) break;
    lines.push(line);
    used += cost;
  }
  if (lines.length === 0) return "";

  const hidden = rendered.length - lines.length;
  const shown = truncate(lines.join(DETAIL_SEPARATOR), budget);
  return hidden > 0 ? `${shown} (+${hidden} more)` : shown;
}

/**
 * The most specific message the body can support, falling back to the status.
 *
 * Against the `body.error ?? body.message` read this replaced in lib/api.ts —
 * every apiFetch caller — it never returns less: an already-specific message
 * comes back unchanged, and a detail is only ever added, or substituted for a
 * string that said nothing, or for validate()'s summary of the same details.
 *
 * That is NOT an absolute. CreateOrgPage and TenantsPage already rendered every
 * issue themselves, prefixed by path, so what they trade is an unbounded list
 * for a capped one — in exchange for dedupe and the internals filter, not for
 * the prefix they already had. MAX_DETAILS is set above the largest top-level
 * field count of any request schema in the repo (6), so the trade never costs
 * the user a field they can fix; a bulk payload is what gets counted instead.
 *
 * @param body     the parsed JSON body, or null when there was none to parse
 * @param status   the HTTP status, used only when the body says nothing useful
 * @param fallback what to say when even the status is all we have
 */
export function apiErrorMessage(body: unknown, status: number, fallback = "Request failed"): string {
  const fields = (body && typeof body === "object" ? body : {}) as ApiErrorBody;
  const candidates = detailCandidates(fields);

  let top = topMessage(body);
  if (candidates.length > 0 && top.toLowerCase().startsWith(VALIDATION_SUMMARY_LEAD)) {
    top = top.slice(0, VALIDATION_SUMMARY_LEAD.length - 1);
  }
  const generic = !top || GENERIC_TOP_MESSAGES.has(top.toLowerCase());

  let rendered: string[] = [];
  for (const candidate of candidates) {
    rendered = renderAll(candidate);
    if (rendered.length > 0) break;
  }

  // A specific `top` is kept whole, so the details get what is left over.
  const budget = generic
    ? MAX_JOINED_CHARS
    : Math.min(MAX_JOINED_CHARS, MAX_MESSAGE_CHARS - top.length - ": ".length);
  const detail = budget >= MIN_DETAIL_CHARS ? joinWithinBudget(rendered, budget) : "";

  if (detail && generic) return detail;
  if (detail) return detail.startsWith(top) ? detail : `${top}: ${detail}`;
  if (top) return top;
  return `${fallback} (HTTP ${status})`;
}
